import { FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { orgApi, type OrgProject } from "../api/orgApi";
import { signIn } from "../lib/auth";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import CustomButton from "../components/CustomButton";

export function ProjectDetailPage() {
  const { orgId = "", projectId = "" } = useParams<{ orgId: string; projectId: string }>();
  const loc = useLocation();
  const nav = useNavigate();

  const [project, setProject] = useState<OrgProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // sign-in form
  const [apiKey, setApiKey] = useState<string>((loc.state as { apiKey?: string } | null)?.apiKey ?? "");
  const [signInErr, setSignInErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const projects = await orgApi.listProjects(orgId);
        setProject(projects.find((p) => p.id === projectId) ?? null);
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, projectId]);

  function onSignIn(e: FormEvent) {
    e.preventDefault();
    if (!apiKey.trim()) {
      setSignInErr("API key is required.");
      return;
    }
    if (/^https?:\/\//.test(apiKey.trim())) {
      setSignInErr("API key must be a token, not a URL. Enter the key provided when you created the project.");
      return;
    }
    signIn(projectId, apiKey.trim());
    nav("/projects", { replace: true });
  }

  if (loading) {
    return (
      <Box className="orgs-page">
        <Typography variant="body2" className="login-subtitle">Loading…</Typography>
      </Box>
    );
  }

  const p = project;

  return (
    <Box className="orgs-page">
      {/* Breadcrumb */}
      <Link to={`/organizations/${orgId}`} className="org-detail-back">
        <img src="/arrow_right.png" alt="" style={{ width: 14, height: 14, verticalAlign: "middle", marginRight: 4, transform: "scaleX(-1)" }} /> Back to organization
      </Link>

      {/* Hero header */}
      <Box className="project-hero">
        <Box className="project-hero-icon">
          {(p?.name ?? "P").charAt(0).toUpperCase()}
        </Box>
        <Box>
          <Typography variant="h4" className="project-hero-title">
            {p?.name ?? projectId}
          </Typography>
          {p?.description && (
            <Typography variant="body1" className="project-hero-desc">
              {p.description}
            </Typography>
          )}
          {p?.created_at && (
            <Typography variant="caption" className="project-hero-meta">
              Created {new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </Typography>
          )}
        </Box>
      </Box>

      {err && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {err}
        </Alert>
      )}

      <Card className="project-card">
        <CardContent sx={{ p: 3 }}>
          {/* Project details */}
          <Typography variant="subtitle2" className="project-card-header">
            Project Information
          </Typography>

          <Box className="project-info-grid">
            <Box className="project-info-item">
              <Typography variant="caption" className="project-info-label">
                Project ID
              </Typography>
              <Box className="project-info-value-box">
                <code className="project-info-code">{projectId}</code>
              </Box>
            </Box>

            {p?.created_at && (
              <Box className="project-info-item">
                <Typography variant="caption" className="project-info-label">
                  Created
                </Typography>
                <Typography variant="body2" className="project-info-value">
                  {new Date(p.created_at).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                </Typography>
              </Box>
            )}
          </Box>

          {p?.settings && Object.keys(p.settings).length > 0 && (
            <>
              <Divider sx={{ my: 2, borderColor: "rgba(203, 46, 186, 0.25)" }} />
              <Typography variant="caption" className="project-info-label">
                Settings
              </Typography>
              <pre className="project-detail-settings">
                {JSON.stringify(p.settings, null, 2)}
              </pre>
            </>
          )}

          <Divider sx={{ my: 3, borderColor: "#e3e3e5" }} />

          {/* Sign-in */}
          <Box component="form" onSubmit={onSignIn}>
            <Typography variant="subtitle2" className="project-card-header" sx={{ mb: 1, textTransform: "none" }}>
              Sign in to this project
            </Typography>
            <Typography variant="body2" className="login-subtitle" sx={{ mb: 3 }}>
              Enter your API key to start exploring code insights for this project.
            </Typography>

            <Box className="login-fields">
              <TextField
                label="API key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="ci_..."
                size="small"
                autoFocus
                className="login-input"
                sx={{ width: 450 }}
              />
              {signInErr && (
                <Alert severity="error" sx={{ py: 0.5 }}>
                  {signInErr}
                </Alert>
              )}
              <CustomButton
                loading={false}
                buttonText={<>Sign in <img src="/arrow_right.png" alt="" style={{ width: 14, height: 14, verticalAlign: "middle", marginLeft: 4 }} /></>}
                buttonTextLoading=""
                handleSubmitClick={() => {}}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
